const hexToRgb = (hex) => {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;

  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
};

// https://www.w3.org/TR/WCAG21/#dfn-relative-luminance
const relativeLuminance = ({ r, g, b }) => {
  const channel = (value) => {
    const srgb = value / 255;

    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };

  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

const contrastRatio = (hexA, hexB) => {
  const [lighter, darker] = [relativeLuminance(hexToRgb(hexA)), relativeLuminance(hexToRgb(hexB))]
    .sort((a, b) => b - a);

  return (lighter + 0.05) / (darker + 0.05);
};

const rgbToHsl = ({ r, g, b }) => {
  const [rN, gN, bN] = [r, g, b].map((v) => v / 255);
  const max = Math.max(rN, gN, bN);
  const min = Math.min(rN, gN, bN);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;

  if (max === rN) {
    h = ((gN - bN) / d) % 6;
  } else if (max === gN) {
    h = (bN - rN) / d + 2;
  } else {
    h = (rN - gN) / d + 4;
  }
  h *= 60;
  if (h < 0) h += 360;

  return { h, s, l };
};

const hslToHex = ({ h, s, l }) => {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const [rP, gP, bP] = (() => {
    if (h < 60) return [c, x, 0];
    if (h < 120) return [x, c, 0];
    if (h < 180) return [0, c, x];
    if (h < 240) return [0, x, c];
    if (h < 300) return [x, 0, c];
    return [c, 0, x];
  })();
  const toHex = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');

  return `#${toHex(rP)}${toHex(gP)}${toHex(bP)}`;
};

/**
 * Lightens `hex` in HSL steps until it reads clearly against `backgroundHex` (WCAG's 4.5:1 minimum for
 * text - `main` is used as a text colour at several call sites, not just as a fill), or gives up after a
 * fixed number of steps rather than looping forever on a colour this can't help (already-light colours,
 * or ones near pure grey, pass through unchanged).
 * ponytail: a flat lightness bump, not a full accessible-palette generator - fine for "a company's dark
 * brand colour is unreadable against the dark theme's own background", revisit if hue/saturation also
 * need to move.
 */
export const ensureMinContrast = (hex, backgroundHex, minRatio = 4.5, maxSteps = 12) => {
  let candidate = hex;

  for (let step = 0; step < maxSteps && contrastRatio(candidate, backgroundHex) < minRatio; step += 1) {
    const hsl = rgbToHsl(hexToRgb(candidate));

    if (hsl.l >= 0.95) {
      break;
    }

    candidate = hslToHex({ ...hsl, l: Math.min(hsl.l + 0.08, 0.95) });
  }

  return candidate;
};
